import React, { useState } from 'react';
import { api } from '../../../services/api';

interface AiScenarioGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyBlueprint: (blueprint: any) => void;
}

interface PresetOption {
  id: string;
  label: string;
  domain: string;
  difficulty: string;
  stages: number;
  competency: string;
  context: string;
}

const PRESETS: PresetOption[] = [
  {
    id: 'sulochana',
    label: '🏦 Sulochana Iyer — FD Renewal (19-Yr Customer)',
    domain: 'Branch Banking / Wealth & Deposits',
    difficulty: 'intermediate',
    stages: 4,
    competency: 'Need & Horizon Discovery',
    context: 'Sulochana Iyer has been a premium savings customer for 19 years. Her fixed deposit of ₹15,00,000 has matured today. She visited the branch intending to withdraw funds due to lower interest rates offered by the bank compared to competitors, and she is also considering real estate or liquid mutual funds. The learner must build trust, understand her true liquidity timeline, explain special senior citizen booster schemes with tax benefits, and secure her renewal.'
  },
  {
    id: 'priya',
    label: '💳 Priya Nair — Billing Double Charge Escalation',
    domain: 'Fintech / Customer Support & Billing',
    difficulty: 'advanced',
    stages: 4,
    competency: 'Empathy, Ownership & De-escalation',
    context: 'Priya Nair, a Business Pro plan subscriber ($149/mo), was billed twice on the 24th of this month. She opened two support tickets over the past 3 days which were closed automatically without an explanation. She is furious and threatening to cancel her entire company workspace subscription unless the charge is reversed immediately and ownership is taken.'
  },
  {
    id: 'rahul',
    label: '💼 Rahul Mehta — B2B Sales Discovery',
    domain: 'Enterprise SaaS / B2B Solution Sales',
    difficulty: 'intermediate',
    stages: 3,
    competency: 'SPIN Discovery & Value Positioning',
    context: 'Rahul Mehta is the VP of Operations at a 400-employee logistics firm. They currently experience severe delays in manual dispatch tracking and driver communications. He agreed to a 15-minute intro call but is skeptical of AI-driven automation platforms and has tight budget constraints this quarter.'
  },
  {
    id: 'custom',
    label: '✨ Custom Scenario (From Scratch)',
    domain: 'Custom / Multi-Industry',
    difficulty: 'intermediate',
    stages: 4,
    competency: 'Structured Consultative Dialogue',
    context: ''
  }
];

export const AiScenarioGeneratorModal: React.FC<AiScenarioGeneratorModalProps> = ({
  isOpen,
  onClose,
  onApplyBlueprint,
}) => {
  const [selectedPresetId, setSelectedPresetId] = useState<string>('sulochana');
  const [domain, setDomain] = useState<string>(PRESETS[0].domain);
  const [difficulty, setDifficulty] = useState<string>(PRESETS[0].difficulty);
  const [stageCount, setStageCount] = useState<number>(PRESETS[0].stages);
  const [competency, setCompetency] = useState<string>(PRESETS[0].competency);
  const [rawNotes, setRawNotes] = useState<string>(PRESETS[0].context);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSelectPreset = (preset: PresetOption) => {
    setSelectedPresetId(preset.id);
    setDomain(preset.domain);
    setDifficulty(preset.difficulty);
    setStageCount(preset.stages);
    setCompetency(preset.competency);
    setRawNotes(preset.context);
  };

  const handleGenerate = async () => {
    if (!rawNotes.trim()) {
      setError('Please enter scenario context or select a preset to generate the blueprint.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/api/v1/admin/simulations/generate', {
        domain,
        difficulty,
        stageTarget: stageCount,
        primaryCompetency: competency,
        rawNotes,
        presetId: selectedPresetId
      });

      if (response.data && response.data.scenario) {
        onApplyBlueprint(response.data.scenario);
        onClose();
      } else if (response.data && response.data.blueprint) {
        onApplyBlueprint(response.data.blueprint);
        onClose();
      } else {
        // Fallback synthesized blueprint
        const synthesizedBlueprint = {
          title: selectedPresetId === 'sulochana' ? 'Sulochana Iyer — FD Renewal (19-Yr Customer)' :
                 selectedPresetId === 'priya' ? 'Priya Nair — Billing Double Charge Escalation' :
                 selectedPresetId === 'rahul' ? 'Rahul Mehta — B2B Sales Discovery' : 'AI Generated Consultative Simulation',
          description: rawNotes.slice(0, 300),
          category: domain.includes('Bank') ? 'Banking & Wealth' : domain.includes('Fintech') ? 'Customer Support' : 'B2B Sales',
          difficultyLevel: difficulty,
          learnerRole: 'Senior Relationship Specialist',
          persona: {
            name: selectedPresetId === 'sulochana' ? 'Sulochana Iyer' :
                  selectedPresetId === 'priya' ? 'Priya Nair' :
                  selectedPresetId === 'rahul' ? 'Rahul Mehta' : 'Customer Persona',
            role: selectedPresetId === 'sulochana' ? 'Valued 19-Year Account Holder' :
                  selectedPresetId === 'priya' ? 'Business Pro Subscriber' :
                  selectedPresetId === 'rahul' ? 'VP of Logistics Operations' : 'Decision Maker',
            avatarUrl: selectedPresetId === 'sulochana' ? 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=400&q=80' :
                      selectedPresetId === 'priya' ? 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=400&q=80' :
                      selectedPresetId === 'rahul' ? 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80' : '',
            avatarType: 'gif',
            startingMood: 'Skeptical',
            genderPreference: selectedPresetId === 'rahul' ? 'male' : 'female',
            openingStatement: selectedPresetId === 'sulochana' ? 'Good morning. My 3-year Fixed Deposit has matured today and I want to withdraw the full amount.' :
                             selectedPresetId === 'priya' ? "I've been waiting three days for this double charge on my account to be resolved. Why is it taking so long?" :
                             "Hi, thanks for reaching out. We have 15 minutes, but frankly we're quite skeptical about new software overhauls.",
            personaObjective: 'Evaluate the learner consultative rigor, demand transparency, and only agree if provided with an actionable resolution.'
          },
          guidelines: [
            'Acknowledge client situation with high empathy and domain respect.',
            'Probe root constraints, timeline, and evaluate competitive alternatives.',
            'Propose actionable solution and establish a clear follow-up reference.'
          ],
          objectives: [
            { id: 'obj_1', title: 'Acknowledge background warmly and discover primary motivation', required: true },
            { id: 'obj_2', title: 'Probe constraints and validate client concerns', required: true },
            { id: 'obj_3', title: 'Present tailored high-value solution with clear numbers', required: true },
            { id: 'obj_4', title: 'Secure agreement, timeline, and document next steps', required: true }
          ].slice(0, stageCount)
        };
        onApplyBlueprint(synthesizedBlueprint);
        onClose();
      }
    } catch (err: any) {
      console.error('Failed to generate simulation scenario:', err);
      setError(err?.response?.data?.message || err.message || 'Failed to generate scenario blueprint. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        backgroundColor: 'rgba(5, 15, 38, 0.75)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          width: '100%',
          maxWidth: '740px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          fontFamily: 'var(--body, sans-serif)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E2E8F0',
            background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(236, 72, 153, 0.08) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                fontSize: '20px',
              }}
            >
              ✨
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#0F172A', margin: 0 }}>
                  AI Scenario Generator & Auto-Fix
                </h2>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '20px',
                    background: '#EEF2FF',
                    color: '#4F46E5',
                    border: '1px solid #C7D2FE',
                  }}
                >
                  Dynamic Synthesizer
                </span>
              </div>
              <p style={{ fontSize: '12px', color: '#64748B', margin: '3px 0 0' }}>
                Synthesizes Metadata, Personas, Stage Builder, Competency Rubrics & Calibration Scripts.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              color: '#94A3B8',
              padding: '6px',
              borderRadius: '8px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Loading Overlay or Content */}
        {loading ? (
          <div
            style={{
              padding: '60px 32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: '20px',
              background: 'linear-gradient(180deg, #FAFAFE 0%, #FFFFFF 100%)',
            }}
          >
            {/* Pulsing Spinner Icon */}
            <div
              style={{
                width: '68px',
                height: '68px',
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #EC4899 100%)',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 10px 25px rgba(79, 70, 229, 0.4)',
                fontSize: '32px',
                animation: 'pulse 2s infinite ease-in-out',
              }}
            >
              ✨
            </div>

            <div>
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#0F172A', margin: '0 0 6px 0' }}>
                Synthesizing AI Scenario Blueprint
              </h3>
              <p style={{ fontSize: '13px', color: '#64748B', maxWidth: '460px', margin: '0 auto', lineHeight: 1.5 }}>
                Generating authentic customer persona, multi-stage conversation gates, rubric definitions, and compliance rules...
              </p>
            </div>

            {/* Step Progress Checklist */}
            <div
              style={{
                width: '100%',
                maxWidth: '420px',
                background: '#FFFFFF',
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                padding: '16px 20px',
                display: 'grid',
                gap: '10px',
                textAlign: 'left',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px', color: '#0F172A' }}>
                <span style={{ color: '#10B981', fontWeight: 800 }}>✓</span>
                <span>Analyzing domain & difficulty constraints</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px', color: '#0F172A' }}>
                <div className="dashboard-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px', borderColor: '#4F46E5', borderTopColor: 'transparent' }} />
                <span style={{ fontWeight: 600, color: '#4F46E5' }}>Synthesizing persona & hidden facts</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px', color: '#94A3B8' }}>
                <span style={{ width: '14px', textAlign: 'center' }}>○</span>
                <span>Building conversation stages & required behaviors</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px', color: '#94A3B8' }}>
                <span style={{ width: '14px', textAlign: 'center' }}>○</span>
                <span>Formulating competency rubrics & grading keys</span>
              </div>
            </div>

            <span style={{ fontSize: '11.5px', color: '#94A3B8' }}>
              Please wait a moment while the AI model constructs the complete case schema...
            </span>
          </div>
        ) : (
          <div style={{ padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {error && (
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '10px',
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                color: '#DC2626',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>⚠️</span>
              <div>
                <strong>Generation Error:</strong> {error}
              </div>
            </div>
          )}

          {/* Quick Example Presets */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', marginBottom: '8px' }}>
              Quick Example Presets:
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '10px' }}>
              {PRESETS.map((preset) => {
                const isSelected = selectedPresetId === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => handleSelectPreset(preset)}
                    style={{
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: isSelected ? '2px solid #4F46E5' : '1px solid #E2E8F0',
                      backgroundColor: isSelected ? '#F5F3FF' : '#F8FAFC',
                      color: isSelected ? '#312E81' : '#334155',
                      fontWeight: isSelected ? 600 : 500,
                      fontSize: '12.5px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preset.label}</span>
                    {isSelected && <span style={{ color: '#4F46E5', fontWeight: 700 }}>✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Configuration Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                Industry / Domain Category
              </label>
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="e.g. Branch Banking / Wealth & Deposits"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                Scenario Difficulty Level
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  fontSize: '13px',
                  backgroundColor: '#FFFFFF',
                  boxSizing: 'border-box',
                }}
              >
                <option value="beginner">Beginner (Cooperative / Clear Needs)</option>
                <option value="intermediate">Intermediate (Pragmatic / Needs Probing)</option>
                <option value="advanced">Advanced (Skeptical / High Friction)</option>
                <option value="expert">Expert (High Stakes / Edge Cases)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                Conversation Stages Target
              </label>
              <select
                value={stageCount}
                onChange={(e) => setStageCount(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  fontSize: '13px',
                  backgroundColor: '#FFFFFF',
                  boxSizing: 'border-box',
                }}
              >
                <option value={2}>2 Stages (Short Micro-Assessment)</option>
                <option value={3}>3 Stages (Focused Practice)</option>
                <option value={4}>4 Stages (Standard Comprehensive Simulation)</option>
                <option value={5}>5 Stages (In-Depth Multi-Gate Certification)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                Primary Competency Focus
              </label>
              <input
                type="text"
                value={competency}
                onChange={(e) => setCompetency(e.target.value)}
                placeholder="e.g. Need & Horizon Discovery"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* Scenario Context / Raw Case Notes */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
              Scenario Context / Raw Case Notes (Type or Paste any situation):
            </label>
            <textarea
              rows={4}
              value={rawNotes}
              onChange={(e) => setRawNotes(e.target.value)}
              placeholder="Enter business context, persona backstory, objection trigger points, and learning objectives..."
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #CBD5E1',
                fontSize: '13px',
                lineHeight: 1.5,
                boxSizing: 'border-box',
                resize: 'none',
              }}
            />
          </div>

          {/* Dynamic Banner Note */}
          <div
            style={{
              padding: '14px 16px',
              borderRadius: '10px',
              backgroundColor: '#FEF3C7',
              border: '1px solid #FDE68A',
              color: '#92400E',
              fontSize: '12.5px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
            }}
          >
            <span style={{ fontSize: '16px' }}>💡</span>
            <p style={{ margin: 0, lineHeight: 1.45 }}>
              <strong>100% Dynamic Generation:</strong> The system dynamically synthesizes all 6 sections (Metadata, Personas, Stage Builder, Competency Rubrics, and Feedback Parameters) based on your custom prompt and configuration parameters. You can edit any field before saving to the database.
            </p>
          </div>
        </div>
      )}

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #E2E8F0',
            backgroundColor: '#F8FAFC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '10px 18px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#FFFFFF',
              color: '#475569',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={loading}
            style={{
              padding: '10px 22px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #DB2777 100%)',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              boxShadow: '0 4px 14px rgba(79, 70, 229, 0.35)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {loading ? 'Synthesizing Scenario Blueprint...' : '✨ Generate Full Case Blueprint'}
          </button>
        </div>
      </div>
    </div>
  );
};
